import {
  Button,
  Col,
  Divider,
  Form,
  message,
  Radio,
  RadioChangeEvent,
  Row,
  Select,
  SelectProps,
  Upload,
  UploadProps,
} from "antd";
import { FC, useEffect, useState } from "react";
import { UploadOutlined, DownloadOutlined } from "@ant-design/icons";
import { RcFile, UploadChangeParam, UploadFile } from "antd/es/upload";
import {
  getCornerSlots,
  getSlots,
  getSystemSlot,
  getSystemSlotsValues,
} from "../../../utils/complication";
import { getKeyByValue } from "../../../utils/helpers";
import http from "../../../services/api/http";
import {
  ComplicationSlotType,
  WFComplicationType,
  WFFaceStyle,
  SystemComplicationPreviewModel,
  WFInformationType,
  ComplicationSystemSlotType,
} from "../../../dto/WatchFace.dto";

const ComplicationSlotsFields: FC<any> = ({
  form,
  complicationType,
  slots,
  setSlots,
  ...args
}: any) => {
  const faceStyle: WFFaceStyle = Form.useWatch("faceStyle", form);
  const availableSlots = getSlots(complicationType, { faceStyle });
  const cornerSlots = getCornerSlots(complicationType, { faceStyle });

  const onChangeFile = (
    type: ComplicationSlotType,
    data: any,
    additionalData: any
  ) => {
    const temp = slots;
    temp[type] = { ...temp[type], file: data, ...additionalData };
    setSlots(temp);
  };
  return (
    <>
      {availableSlots?.map((slot) => {
        const slotData = slots[slot];
        return (
          <div key={slot} style={{ marginBottom: "16px" }}>
            <Slot
              type={slot}
              faceStyle={faceStyle}
              complicationType={complicationType}
              defaultValue={slots[getSystemSlot(slot)]?.informationType}
              defaultFile={
                slotData?.fileUrl
                  ? {
                      uid: slotData?.complicationId,
                      name: `${slot} - ${slotData?.complicationId}`,
                      status: "done",
                      url: slotData?.previewUrl,
                    }
                  : null
              }
              complicationId={slotData?.complicationId}
              onChangeFile={onChangeFile}
            />
          </div>
        );
      })}

      <Divider />

      {cornerSlots?.map((slot) => {
        const slotData = slots[slot];
        return (
          <div key={slot} style={{ marginBottom: "16px" }}>
            <Slot
              type={slot}
              faceStyle={faceStyle}
              complicationType={complicationType}
              defaultValue={slots[getSystemSlot(slot)]?.informationType}
              defaultFile={
                slotData?.fileUrl
                  ? {
                      uid: slotData?.complicationId,
                      name: `${slot} - ${slotData?.complicationId}`,
                      status: "done",
                      url: slotData?.previewUrl,
                    }
                  : null
              }
              complicationId={slotData?.complicationId}
              onChangeFile={onChangeFile}
            />
          </div>
        );
      })}
    </>
  );
};

const Slot: FC<any> = ({
  type,
  onChangeFile,
  defaultFile,
  faceStyle,
  complicationType,
  defaultValue,
  complicationId,
}: {
  type: ComplicationSlotType;
  onChangeFile: (
    type: ComplicationSlotType | ComplicationSystemSlotType,
    file: any,
    additionalData?: any
  ) => void;
  defaultFile: any;
  faceStyle: WFFaceStyle;
  complicationType: WFComplicationType;
  defaultValue: WFInformationType;
  complicationId?: string;
}) => {
  const isDisableNew =
    type === ComplicationSlotType.SubdialTop &&
    complicationType === WFComplicationType.California;
  const [value, setValue] = useState(isDisableNew ? "select" : "new");
  const [fileList, setFileList] = useState<UploadFile[]>([]);

  const handleOnChangeValue = ({ target: { value } }: RadioChangeEvent) => {
    setValue(value);
  };

  const slotsOptions: SelectProps["options"] = getSystemSlotsValues(
    complicationType,
    type
  )?.map((value) => ({
    label: getKeyByValue(WFInformationType, value),
    value: value,
  }));

  const options = [
    { label: "New", value: "new", disabled: isDisableNew },
    {
      label: "Select",
      value: "select",
      disabled: !Boolean(slotsOptions?.length),
    },
  ];

  const beforeUpload = (file: RcFile) => {
    const isSupportType = ["application/zip"].includes(file.type);
    if (!isSupportType) {
      message.error("Unsupported file type");
    }

    return isSupportType;
  };

  const handleChange: UploadProps["onChange"] = async (
    info: UploadChangeParam<UploadFile>
  ) => {
    if (info.file.status === "uploading") {
      info.file.status = "done";
      setFileList(info.fileList);
      onChangeFile(type, info.file.originFileObj);
    }

    if (info.file.status === "removed") {
      setFileList([]);
      onChangeFile(type, null);
    }
  };

  const handleOnChangeSelect = (data: WFInformationType) => {
    const additionalData: Omit<SystemComplicationPreviewModel, "familyType"> = {
      informationType: data,
    };
    onChangeFile(getSystemSlot(type), null, additionalData);
  };

  const handleArchiveDownload = async () => {
    if (!complicationId) return;

    try {
      const response = await http.get(`/complications/${complicationId}/file`, {
        responseType: "blob",
      });

      const blob = new Blob([response.data], { type: "application/zip" });
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = `${complicationId}.zip`;
      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      message.error("Failed to download archive");
      console.error("Download error:", error);
    }
  };

  useEffect(() => {
    if (defaultFile) {
      setFileList([defaultFile]);
      setValue("new");
    }
  }, [defaultFile]);

  return (
    <div
      style={{
        border: "1px solid #d9d9d9",
        borderRadius: "6px",
        padding: "16px",
        backgroundColor: "#fafafa",
        boxShadow: "0 1px 2px rgba(0, 0, 0, 0.03)",
      }}
    >
      <Row>
        <Col span={24}>
          <h5 style={{ marginTop: 0, marginBottom: "12px" }}>
            {getKeyByValue(ComplicationSlotType, type)}
          </h5>
        </Col>
      </Row>

      {complicationId && (
        <Row style={{ marginBottom: "12px" }}>
          <Col span={24}>
            <div
              style={{
                padding: "8px 12px",
                backgroundColor: "#f0f0f0",
                borderRadius: "4px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                border: "1px solid #e8e8e8",
              }}
            >
              <span style={{ fontWeight: "bold" }}>
                Complication ID:{" "}
                <span style={{ color: "#1890ff" }}>{complicationId}</span>
              </span>
              <Button
                icon={<DownloadOutlined />}
                size="small"
                onClick={handleArchiveDownload}
              >
                Download Archive
              </Button>
            </div>
          </Col>
        </Row>
      )}

      <Row style={{ marginBottom: "12px" }}>
        <Col span={24}>
          <Radio.Group
            options={options}
            onChange={handleOnChangeValue}
            value={value}
            optionType="button"
          />
        </Col>
      </Row>
      <Row>
        {value === "new" && (
          <Col span={24}>
            <Upload
              name={type}
              fileList={fileList}
              onChange={handleChange}
              beforeUpload={beforeUpload}
              maxCount={1}
              listType="picture"
            >
              <Button icon={<UploadOutlined />}>Click to Upload</Button>
            </Upload>
          </Col>
        )}
        {value === "select" && (
          <Col span={24}>
            <Select
              onChange={handleOnChangeSelect}
              allowClear
              style={{ width: "100%" }}
              placeholder="Complication Slot"
              options={slotsOptions}
              defaultValue={defaultValue}
            />
          </Col>
        )}
      </Row>
    </div>
  );
};

export default ComplicationSlotsFields;
